import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { Image3D } from '~/domain/patient/image3d';
import {
    useSelectedPrimaryImage,
    useSelectedSecondaryImage,
} from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { dicomProcessorVersionState } from '~/state/atoms/dicomProcessorAtoms';

/**
 * Resolves an image by its series UID: the loaded primary/secondary first,
 * else the patient's list. A readout stays labelled in the unit of the series
 * it was measured on, whatever the viewports show now; undefined once the
 * series is gone from the patient, which the caller has to handle: the unit
 * and the display scale both come from the image.
 */
export function useImageForSeries(): (
    seriesInstanceUid: string
) => Image3D | undefined {
    const primaryImage = useSelectedPrimaryImage();
    const secondaryImage = useSelectedSecondaryImage();
    const patient = useSelectedPatient();
    const dicomProcessorVersion = useRecoilValue(dicomProcessorVersionState);

    return useCallback(
        (seriesInstanceUid: string) =>
            [primaryImage, secondaryImage].find(
                (image) => image?.seriesUid === seriesInstanceUid
            ) ??
            patient?.images.find(
                (image) => image.seriesUid === seriesInstanceUid
            ),
        // dicomProcessorVersion is a refresh tripwire, not read in the body.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [primaryImage, secondaryImage, patient, dicomProcessorVersion]
    );
}
